import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        Porównujesz oferty dwóch banków. Pierwszy ma oprocentowanie 6,5%, drugi 6,8% — wybierasz
        oczywiście pierwszy, prawda? Niekoniecznie. Może się okazać, że tańszy w rzeczywistości
        jest ten drugi, jeśli ma niższą prowizję, ubezpieczenia i inne opłaty. Tu właśnie wkracza
        RRSO — jedyna liczba, która pokazuje prawdziwy koszt kredytu.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co to jest RRSO?</h2>
      <p>
        RRSO (Rzeczywista Roczna Stopa Oprocentowania) to wskaźnik, który pokazuje całkowity
        koszt kredytu w ujęciu rocznym, wyrażony w procentach. W przeciwieństwie do "zwykłego"
        oprocentowania, RRSO uwzględnia <strong>wszystkie</strong> koszty związane z kredytem:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Oprocentowanie nominalne</strong> — czyli <BlogLink slug="co-to-jest-wibor">WIBOR</BlogLink> + marża banku</li>
        <li><strong>Prowizja banku</strong> za udzielenie kredytu</li>
        <li><strong>Ubezpieczenia</strong> wymagane przez bank (na życie, niskiego wkładu)</li>
        <li><strong>Koszty wyceny nieruchomości</strong> i inne opłaty bankowe</li>
        <li><strong>Częstotliwość spłat</strong> — miesięczna, kwartalna itp.</li>
      </ul>
      <p className="mt-3">
        Banki w Polsce mają obowiązek podawania RRSO w każdej ofercie kredytowej —
        wynika to z ustawy o kredycie hipotecznym z 2017 roku.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czym RRSO różni się od oprocentowania?</h2>
      <p>
        Najważniejsza różnica: <strong>oprocentowanie pokazuje tylko odsetki, RRSO pokazuje
        wszystko, co zapłacisz bankowi</strong>.
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Przykład — kredyt 400 000 zł na 25 lat:</p>
        <p className="text-sm">
          <strong>Bank A:</strong> oprocentowanie 6,5%, prowizja 2%, ubezpieczenie 0,5% rocznie<br/>
          → RRSO ≈ <strong>7,2%</strong>
        </p>
        <p className="text-sm mt-2">
          <strong>Bank B:</strong> oprocentowanie 6,8%, prowizja 0%, brak ubezpieczenia<br/>
          → RRSO ≈ <strong>7,0%</strong>
        </p>
        <p className="text-sm mt-2 font-semibold">
          Wniosek: Bank B jest tańszy, mimo że ma wyższe oprocentowanie!
        </p>
      </div>

      <p className="mt-3">
        To dlatego porównywanie ofert tylko po oprocentowaniu jest pułapką. RRSO pokazuje
        prawdziwą wartość — i tylko ten wskaźnik powinien decydować o wyborze.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} section="interest-rate">
          Sprawdź ratę dla różnych oprocentowań
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Jak liczy się RRSO?</h2>
      <p>
        Wzór na RRSO jest skomplikowany — opiera się na metodzie zdyskontowanych przepływów
        pieniężnych. Na szczęście nie musisz go obliczać ręcznie. Każdy bank ma obowiązek
        podać Ci RRSO w ofercie. Co warto wiedzieć:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>RRSO jest zawsze wyższe niż oprocentowanie nominalne</strong> — bo zawiera dodatkowe koszty</li>
        <li><strong>Im krótszy okres kredytu, tym wyższe RRSO</strong> — koszty stałe (jak prowizja) "rozkładają się" na mniej miesięcy</li>
        <li><strong><BlogLink slug="raty-rowne-czy-malejace">Raty malejące</BlogLink> mają wyższe RRSO niż równe</strong> — choć całkowity koszt jest niższy (paradoks!)</li>
        <li><strong>RRSO zakłada brak nadpłat</strong> — jeśli planujesz <BlogLink slug="nadplata-kredytu-hipotecznego">nadpłacać</BlogLink>, faktyczny koszt będzie niższy</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czy RRSO to najlepszy wskaźnik?</h2>
      <p>
        RRSO ma swoje ograniczenia. Choć jest najlepszym narzędziem do porównywania ofert
        w jednym banku lub między bankami, ma kilka pułapek:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Nie uwzględnia kosztów notarialnych i PCC</strong> — to płacisz osobno, nie do banku</li>
        <li><strong>Zakłada utrzymanie warunków przez cały okres</strong> — przy <BlogLink slug="stale-czy-zmienne-oprocentowanie">zmiennym oprocentowaniu</BlogLink> realne RRSO będzie inne</li>
        <li><strong>Nie pokazuje wysokości raty</strong> — dwa kredyty z tym samym RRSO mogą mieć różne raty (zależy od okresu)</li>
        <li><strong>Krótszy okres = wyższe RRSO</strong> — co może błędnie sugerować, że dłuższy kredyt jest tańszy (a w rzeczywistości zapłacisz więcej odsetek)</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co sprawdzać oprócz RRSO?</h2>
      <p>
        RRSO to dobry punkt wyjścia, ale przy wyborze kredytu warto patrzeć też na:
      </p>
      <ol className="list-decimal pl-6 space-y-2 mt-3">
        <li><strong>Całkowity koszt kredytu</strong> — suma wszystkich rat plus opłaty. To realna kwota, jaką oddasz bankowi</li>
        <li><strong>Wysokość raty</strong> — najważniejsze dla budżetu domowego</li>
        <li><strong>Warunki wcześniejszej spłaty</strong> — czy są opłaty? Po jakim czasie?</li>
        <li><strong>Marża banku</strong> — w długim okresie to ona decyduje (WIBOR będzie się zmieniał)</li>
        <li><strong>Wymagania dodatkowe</strong> — konto w banku, ubezpieczenia, karty kredytowe</li>
        <li><strong>Elastyczność</strong> — czy można zmienić typ rat, wydłużyć okres, refinansować?</li>
      </ol>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Praktyczna zasada wyboru</h2>
      <p>
        Mając trzy oferty, porównaj je w tej kolejności:
      </p>
      <ol className="list-decimal pl-6 space-y-2 mt-3">
        <li><strong>Czy mieszczę się w racie?</strong> — jeśli nie, oferta odpada niezależnie od RRSO</li>
        <li><strong>RRSO</strong> — wybierz najniższe spośród pozostałych</li>
        <li><strong>Sprawdź warunki dodatkowe</strong> — wymagania banku, opłaty za nadpłatę, elastyczność</li>
        <li><strong>Renegocjuj marżę</strong> — nawet 0,2 p.p. mniej obniży RRSO o porównywalną wartość</li>
      </ol>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} section="affordability">
          Sprawdź czy mieścisz się w racie
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">RRSO w 2026 roku — czego się spodziewać?</h2>
      <p>
        Po <BlogLink slug="obnizki-stop-procentowych-2026">obniżkach stóp procentowych</BlogLink> w
        2026 roku, RRSO kredytów hipotecznych spadło z poziomu 9-10% (typowych dla 2023 r.)
        do 6,5-7,5% obecnie. Banki konkurują o klientów promocjami "0% prowizji", co
        bezpośrednio obniża RRSO.
      </p>
      <p className="mt-3">
        Jeśli masz stary kredyt z RRSO powyżej 8%, warto rozważyć <BlogLink slug="refinansowanie-kredytu-hipotecznego">refinansowanie</BlogLink> —
        nawet 1 p.p. różnicy w RRSO przy długim okresie spłaty oznacza dziesiątki tysięcy
        złotych oszczędności.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        RRSO to najważniejszy wskaźnik przy porównywaniu kredytów — pokazuje rzeczywisty
        koszt z uwzględnieniem wszystkich opłat, nie tylko odsetek. Niskie oprocentowanie
        nie znaczy nic, jeśli bank "ukrywa" koszty w prowizjach i ubezpieczeniach. Zawsze
        proś o RRSO i porównuj oferty właśnie po tym wskaźniku. Pamiętaj jednak, że RRSO
        zakłada utrzymanie warunków przez cały okres — w rzeczywistości <BlogLink slug="co-to-jest-wibor">WIBOR</BlogLink> się
        zmienia, więc realny koszt może być inny.
      </p>
      <p className="mt-3">
        Porównywanie ofert po samym oprocentowaniu zamiast po RRSO to jeden z 7 <BlogLink slug="najczestsze-bledy-kredyt-hipoteczny">najczęstszych
        błędów polskich kredytobiorców</BlogLink> — różnica między najtańszym a najdroższym
        kredytem o tym samym oprocentowaniu nominalnym może wynosić nawet 30 000-50 000 zł.
      </p>

      <div className="mt-4">
        <CalculatorLink section="interest-rate">
          Oblicz całkowity koszt swojego kredytu
        </CalculatorLink>
      </div>
    </>
  )
}
